import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import './Tabs_.scss';

import { Icon, IconType } from '@components/Icon';
import { useDerivedState } from '@hooks/useDerivedState';
import classNames from 'classnames';

export enum TabModifier {
    WithoutPaddings = 'tabs_without_paddings',
}

interface Props {
    items: TabItems[];
    dataTest: string;
    activeIndex?: number;
    onTabChange?(index: number): void;
    modifiers?: TabModifier[];
}

export interface TabItems {
    key: number;
    label: string;
    dataTest?: string;
}

interface InkStyle {
    left: number;
    width: number;
}

export function Tabs({ items, dataTest, activeIndex, onTabChange, modifiers = [] }: Props) {
    const [activeTabIndex, setActiveTabIndex] = useState<number>(activeIndex ?? items[0]?.key);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [scrollState, setScrollState] = useState({ atStart: true, atEnd: false });
    const [inkStyle, setInkStyle] = useState<InkStyle>({ left: 0, width: 0 });

    const rootRef = useRef<HTMLDivElement>(null);
    const navWrapRef = useRef<HTMLDivElement>(null);
    const navListRef = useRef<HTMLDivElement>(null);
    const tabRefs = useRef<Record<number, HTMLButtonElement | null>>({});

    useDerivedState(() => {
        if (activeIndex !== undefined) {
            setActiveTabIndex(activeIndex);
        }
    }, [activeIndex]);

    const updateInkStyle = useCallback(() => {
        const activeTab = tabRefs.current[activeTabIndex];
        const navList = navListRef.current;

        if (!activeTab || !navList) {
            setInkStyle({ left: 0, width: 0 });

            return;
        }

        const left = activeTab.offsetLeft;
        const width = activeTab.offsetWidth;

        setInkStyle({ left, width });
    }, [activeTabIndex]);

    useLayoutEffect(() => {
        updateInkStyle();
    }, [updateInkStyle, items]);

    useEffect(() => {
        const navWrap = navWrapRef.current;
        const root = rootRef.current;
        const navList = navListRef.current;
        if (!navWrap || !root || !navList) return;

        const checkState = () => {
            const maxScroll = Math.max(0, navWrap.scrollWidth - navWrap.clientWidth);
            setIsOverflowing(maxScroll > 0);
            setScrollState({
                atStart: navWrap.scrollLeft <= 1,
                atEnd: navWrap.scrollLeft >= maxScroll - 1,
            });
        };

        const handleResize = () => {
            checkState();
            updateInkStyle();
        };

        const resizeObserver = new ResizeObserver(handleResize);
        resizeObserver.observe(root);
        resizeObserver.observe(navWrap);
        resizeObserver.observe(navList);
        navWrap.addEventListener('scroll', checkState, { passive: true });
        checkState();
        updateInkStyle();

        return () => {
            resizeObserver.disconnect();
            navWrap.removeEventListener('scroll', checkState);
        };
    }, [items, updateInkStyle]);

    const handleTabClick = useCallback(
        (key: number) => {
            setActiveTabIndex(key);
            onTabChange?.(key);
        },
        [onTabChange],
    );

    const scrollStep = 150;

    const scrollBy = useCallback((delta: number) => {
        navWrapRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
    }, []);

    const scrollLeft = useCallback(() => scrollBy(-scrollStep), [scrollBy]);
    const scrollRight = useCallback(() => scrollBy(scrollStep), [scrollBy]);

    return (
        <div className={'tabs_wrapper'}>
            <div
                ref={rootRef}
                className={classNames(
                    'tabs_component_root_new',
                    isOverflowing && 'tabs_has_overflow_buttons',
                    ...modifiers,
                )}
                data-test={dataTest}
                data-testid={dataTest}
            >
                {isOverflowing && (
                    <div
                        className={classNames('tabs_nav_button', scrollState.atStart && 'tabs_nav_button_disabled')}
                        onClick={scrollLeft}
                    >
                        <div className={'tabs_nav_button_icon'}>
                            <Icon type={IconType.ARROW_LEFT} />
                        </div>
                    </div>
                )}
                <div
                    ref={navWrapRef}
                    className={'tabs_nav_wrap'}
                >
                    <div
                        ref={navListRef}
                        className={'tabs_nav_list'}
                    >
                        {items.map(({ key, label, dataTest }) => (
                            <button
                                key={key}
                                ref={element => {
                                    tabRefs.current[key] = element;
                                }}
                                type={'button'}
                                className={classNames('tabs_tab', activeTabIndex === key && 'tabs_tab_active')}
                                onClick={() => handleTabClick(key)}
                                data-testid={dataTest}
                            >
                                <span
                                    className={'tabs_tab_label'}
                                    data-text={label}
                                >
                                    {label}
                                </span>
                            </button>
                        ))}
                        <div
                            className={'tabs_nav_ink'}
                            style={{ left: `${inkStyle.left}px`, width: `${inkStyle.width}px` }}
                        />
                    </div>
                </div>
                {isOverflowing && (
                    <div
                        className={classNames(
                            'tabs_nav_button',
                            'tabs_nav_button_next',
                            scrollState.atEnd && 'tabs_nav_button_disabled',
                        )}
                        onClick={scrollRight}
                    >
                        <div className={'tabs_nav_button_icon'}>
                            <Icon type={IconType.ARROW_LEFT} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
